import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import { SAVED_REPORT_DETAIL_COLUMNS } from './use-saved-reports';

/**
 * ONE SAVED REPORT, read for its detail screen — M10 / S10-05.
 *
 * **THE SNAPSHOT IS THE ONLY CONTENT SOURCE.** This reads one row of
 * `saved_report` and nothing else: no Project, no Capture, no photo, no
 * branding. A saved report describes itself, permanently.
 *
 * **`ready` ONLY.** A reservation that never completed is not a report and must
 * not be openable — its object may not even exist.
 */

export interface SavedReportDetail {
  readonly id: string;
  readonly created_at: string;
  readonly report_date: string;
  readonly page_count: number;
  readonly filename: string;
  readonly byte_size: number;
  readonly project_id: string;
  readonly owner_user_id: string;
  readonly snapshot: unknown;
  readonly snapshot_version: number;
}

export type SavedReportStatus = 'loading' | 'ready' | 'failed';

export interface SavedReportState {
  readonly status: SavedReportStatus;
  readonly report: SavedReportDetail | null;
  readonly error: string | null;
}

export function useSavedReport(reportId: string): SavedReportState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<SavedReportStatus>(client ? 'loading' : 'failed');
  const [report, setReport] = useState<SavedReportDetail | null>(null);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fetchReport = useCallback(async (): Promise<
    { kind: 'ready'; row: SavedReportDetail } | { kind: 'failed' }
  > => {
    if (!client) return { kind: 'failed' };
    const { data, error: queryError } = await client
      .from('saved_report')
      .select(SAVED_REPORT_DETAIL_COLUMNS)
      .eq('id', reportId)
      .eq('state', 'ready')
      .maybeSingle();

    if (queryError || !data) {
      if (queryError) reportError(queryError, 'saved-report.load');
      return { kind: 'failed' };
    }
    return { kind: 'ready', row: data };
  }, [client, reportId]);

  const apply = useCallback(
    (result: { kind: 'ready'; row: SavedReportDetail } | { kind: 'failed' }) => {
      if (!alive.current) return;
      if (result.kind === 'failed') {
        setReport(null);
        setError('This saved report could not be opened.');
        setStatus('failed');
        return;
      }
      setReport(result.row);
      setError(null);
      setStatus('ready');
    },
    [],
  );

  const generation = useRef(0);
  const load = useCallback(async (): Promise<void> => {
    if (!client) return;
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchReport();
    if (ticket !== generation.current) return;
    apply(result);
  }, [apply, client, fetchReport]);

  useEffect(() => {
    void load();
  }, [load]);

  return { status, report, error };
}
